/**
 * Protection de la connexion (espace contributeur, espace équipe, /admin).
 *
 * Tous les comptes (contributeurs, éditeurs, administrateurs) sont dans la
 * même collection Payload `users` et se connectent par /api/users/login.
 * Ce module réunit les règles appliquées par les hooks de cette collection
 * (src/collections/Users.ts) et par /api/espace/forgot-password :
 *
 *  - plafond de tentatives par adresse IP (en plus du verrouillage du compte
 *    après 5 essais, qui est stocké en base) ;
 *  - réponse identique, dans le même délai, qu'une adresse existe ou non,
 *    et que le compte soit verrouillé ou non (anti-énumération) ;
 *  - longueur minimale du mot de passe vérifiée côté serveur ;
 *  - témoin « session ouverte » lisible par le navigateur, sans valeur
 *    secrète, pour que l'en-tête du site affiche « Mon espace ».
 *
 * Aucune dépendance à Payload ni à Next : testable seul
 * (scripts/tests/auth-securite.test.ts).
 */

/** Plafonds par adresse IP : [nombre, fenêtre en ms]. */
export const PLAFONDS_AUTH = {
  /** Connexions ÉCHOUÉES par IP (les réussites ne comptent pas). */
  connexion: [20, 15 * 60_000],
  /** Demandes « mot de passe oublié » par IP. */
  oubliIp: [10, 60 * 60_000],
  /** Demandes « mot de passe oublié » pour une même adresse email. */
  oubliEmail: [3, 60 * 60_000],
  /** Liens de définition de mot de passe refusés (jeton invalide ou expiré) par IP. */
  reinitialisation: [20, 15 * 60_000],
} as const satisfies Record<string, readonly [number, number]>

/**
 * Durée minimale d'une connexion refusée. Un email inconnu est refusé sans
 * calcul de mot de passe (quelques ms), un email connu après PBKDF2
 * (25 000 itérations, quelques dizaines de ms) : sans ce plancher, le temps
 * de réponse dirait si le compte existe.
 */
export const DUREE_MIN_ECHEC_MS = 600

/** Longueurs acceptées pour un mot de passe (vérifiées côté serveur). */
export const MDP_MIN = 8
export const MDP_MAX = 256

/** Témoin non secret « une session est ouverte » (lu par l'en-tête du site). */
export const TEMOIN_SESSION = 'sc-connecte'

/** Message unique pour toute connexion refusée (inconnu, mauvais mot de passe, verrouillé). */
export const MSG_CONNEXION_REFUSEE = 'Email ou mot de passe incorrect.'

/** Adresse IP du client, ou null si la requête n'en porte pas (appel interne). */
export function ipClient(headers: Headers | undefined | null): string | null {
  if (!headers || typeof headers.get !== 'function') return null
  const reelle = headers.get('x-real-ip')?.trim()
  if (reelle) return reelle
  const transmise = headers.get('x-forwarded-for')?.split(',')[0]?.trim()
  return transmise || null
}

/** Email normalisé comme Payload le fait (minuscules, sans espaces). */
export function emailNormalise(v: unknown): string {
  return typeof v === 'string' ? v.trim().toLowerCase() : ''
}

/** Erreur de mot de passe à renvoyer, ou null s'il est acceptable. */
export function erreurMotDePasse(v: unknown): string | null {
  // Pas de mot de passe dans la requête (ou champ vide, que Payload ignore
  // à la modification d'un compte) : rien à vérifier.
  if (typeof v !== 'string' || v === '') return null
  if (v.length < MDP_MIN) return `Le mot de passe doit contenir au moins ${MDP_MIN} caractères.`
  if (v.length > MDP_MAX) return `Le mot de passe ne doit pas dépasser ${MDP_MAX} caractères.`
  return null
}

/** Vrai si l'erreur de Payload signale un compte verrouillé (LockedAuth). */
export function estErreurVerrouillage(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const nom = (e as { name?: unknown }).name
  if (nom === 'LockedAuth') return true
  const msg = String((e as { message?: unknown }).message || '').toLowerCase()
  return /locked|verrouill/.test(msg)
}

/** Vrai si l'erreur est un refus de connexion (identifiants ou verrouillage). */
export function estRefusConnexion(e: unknown): boolean {
  if (!e || typeof e !== 'object') return false
  const nom = (e as { name?: unknown }).name
  if (nom === 'AuthenticationError' || estErreurVerrouillage(e)) return true
  const msg = String((e as { message?: unknown }).message || '').toLowerCase()
  return /email or password|mot de passe fourni est incorrect/.test(msg)
}

/** Attend que `dureeMs` se soit écoulée depuis `debut` (horodatage en ms). */
export async function attendreDepuis(debut: number, dureeMs: number = DUREE_MIN_ECHEC_MS): Promise<void> {
  const reste = debut + dureeMs - Date.now()
  if (reste > 0) await new Promise((r) => setTimeout(r, reste))
}

/** Le site sert-il en HTTPS ? (témoins « Secure ») */
export function siteEnHttps(url: string | undefined = process.env.NEXT_PUBLIC_SITE_URL): boolean {
  return !url || url.startsWith('https://')
}

/** En-tête Set-Cookie du témoin de session (valeur « 1 », rien de secret). */
export function cookieTemoinSession(dureeSecondes: number, secure: boolean = siteEnHttps()): string {
  return `${TEMOIN_SESSION}=1; Path=/; Max-Age=${Math.max(0, Math.floor(dureeSecondes))}; SameSite=Lax${secure ? '; Secure' : ''}`
}

/** En-tête Set-Cookie qui efface le témoin de session. */
export function cookieTemoinEfface(secure: boolean = siteEnHttps()): string {
  return `${TEMOIN_SESSION}=; Path=/; Max-Age=0; SameSite=Lax${secure ? '; Secure' : ''}`
}

/** Lecture du témoin dans une chaîne `document.cookie`. */
export function temoinSessionPresent(cookies: string | undefined | null): boolean {
  return new RegExp(`(?:^|;\\s*)${TEMOIN_SESSION}=1(?:;|$)`).test(cookies || '')
}
